import { type FormEvent, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";

import type { DatasetOptions, DatasetResource, SourcePreviewResponse } from "@prequel/import-react";
import {
  DATASET_FREQUENCY_MINUTES,
  useCreateDataset,
  usePreviewSourceRows,
  useSourceTables,
  useUpdateDataset,
} from "@prequel/import-react";
import {
  Alert,
  AlertKind,
  Button,
  ButtonStyle,
  type ComboboxListItem,
  type DropdownListItem,
  type SearchableDropdownListItem,
  Spinner,
} from "@prequel/shared-components";

import StepEdit from "./StepEdit";
import StepOne from "./StepOne";
import StepTwo from "./StepTwo";
import type { DatasetFormStep, DatasetKind, DatasetMethod } from "./types";
import { buildPreviewRequest, buildUpdateDatasetOptions, getFrequencyLabel, getPreviewRequestKey } from "./utils";

import { getErrorMessage } from "@/api/helpers";
import { useAllProviders } from "@/api/import/providers";
import { useAllSources } from "@/api/import/sources";
import { useOrg } from "@/api/queries/orgs";
import { FormFooter } from "@/components/import/FormLayout";
import { useToast } from "@/providers/ToastProvider";

const DEFAULT_FREQUENCY_MINUTES = 1440;

const SYNC_FREQUENCY_ITEMS: DropdownListItem<string>[] = DATASET_FREQUENCY_MINUTES.map((minutes) => ({
  key: `${minutes}`,
  text: getFrequencyLabel(minutes),
}));

type DatasetFormProps = {
  datasetId?: string;
  dataset?: DatasetResource;
  cancelPath?: string;
  onSaved?: () => void;
};

const DatasetForm = ({
  datasetId,
  dataset: existingDataset,
  cancelPath = "/import/datasets",
  onSaved,
}: DatasetFormProps) => {
  const navigate = useNavigate();
  const { addToast } = useToast();
  const formRef = useRef<HTMLFormElement>(null);
  const { data: org } = useOrg();
  const providersQuery = useAllProviders();
  const isEditing = !!datasetId;
  const [step, setStep] = useState<DatasetFormStep>(1);
  const [providerID, setProviderID] = useState("");
  const [sourceID, setSourceID] = useState("");
  const [method, setMethod] = useState<DatasetMethod>("table");
  const [sourceTable, setSourceTable] = useState("");
  const [sqlQuery, setSqlQuery] = useState("");
  const [preview, setPreview] = useState<SourcePreviewResponse>();
  const [previewRequestKey, setPreviewRequestKey] = useState("");
  const [previewError, setPreviewError] = useState<string>();
  const [datasetKind, setDatasetKind] = useState<DatasetKind>("dimension");
  const [primaryKeyColumn, setPrimaryKeyColumn] = useState("");
  const [lastModifiedColumn, setLastModifiedColumn] = useState("");
  const [softDeleteColumn, setSoftDeleteColumn] = useState("");
  const [name, setName] = useState(existingDataset?.name ?? "");
  const [frequencyMinutes, setFrequencyMinutes] = useState(
    `${existingDataset?.frequency_minutes ?? DEFAULT_FREQUENCY_MINUTES}`,
  );
  const [isEnabled, setIsEnabled] = useState(existingDataset?.is_enabled ?? true);

  const providerItems = useMemo<SearchableDropdownListItem<string>[]>(
    () =>
      providersQuery.items.map((provider) => ({
        key: provider.id,
        text: `${provider.name} (${provider.id})`,
        searchValue: `${provider.name} ${provider.id}`,
      })),
    [providersQuery.items],
  );
  const selectedProvider = providerItems.find((item) => item.key === providerID);
  const sourcesQuery = useAllSources(providerID || undefined, { enabled: !!providerID });
  const sourceItems = useMemo<SearchableDropdownListItem<string>[]>(
    () =>
      providerID
        ? sourcesQuery.items.map((source) => ({
            key: source.id,
            text: `${source.name} (${source.id})`,
            searchValue: `${source.name} ${source.id}`,
          }))
        : [],
    [providerID, sourcesQuery.items],
  );
  const selectedSource = sourceItems.find((item) => item.key === sourceID);
  const sourceTablesQuery = useSourceTables(sourceID && method === "table" ? sourceID : undefined);
  const previewRows = usePreviewSourceRows();
  const createDataset = useCreateDataset();
  const updateDataset = useUpdateDataset();
  const tableSuggestions = useMemo<ComboboxListItem[]>(
    () =>
      sourceTablesQuery.tables
        ?.map((table) => `${table.namespace}.${table.name}`)
        .sort((a, b) => a.localeCompare(b))
        .map((qualifiedName) => ({
          key: qualifiedName,
          text: qualifiedName,
        })) ?? [],
    [sourceTablesQuery.tables],
  );
  const previewRequest = useMemo(
    () => buildPreviewRequest(method, sourceTable, sqlQuery),
    [method, sourceTable, sqlQuery],
  );
  const currentPreviewRequestKey = useMemo(
    () => (sourceID && previewRequest ? `${sourceID}:${getPreviewRequestKey(previewRequest)}` : ""),
    [previewRequest, sourceID],
  );
  const hasSuccessfulCurrentPreview = !!preview && previewRequestKey === currentPreviewRequestKey;
  const syncFrequencyItems = useMemo(
    () => SYNC_FREQUENCY_ITEMS.filter((item) => Number(item.key) >= (org?.fastest_allowed_frequency_minutes ?? 0)),
    [org?.fastest_allowed_frequency_minutes],
  );
  const selectedFrequency =
    syncFrequencyItems.find((item) => item.key === frequencyMinutes) ??
    syncFrequencyItems.find((item) => item.key === `${DEFAULT_FREQUENCY_MINUTES}`) ??
    SYNC_FREQUENCY_ITEMS[SYNC_FREQUENCY_ITEMS.length - 1];
  const canPreview = !!sourceID && !!previewRequest && !previewRows.isPreviewing;
  const canCreate = hasSuccessfulCurrentPreview && !!primaryKeyColumn && !createDataset.isCreating;

  useEffect(() => {
    if (!hasSuccessfulCurrentPreview && step === 2) {
      setStep(1);
    }
  }, [hasSuccessfulCurrentPreview, step]);

  const resetPreviewState = () => {
    setPreview(undefined);
    setPreviewRequestKey("");
    setPreviewError(undefined);
    setPrimaryKeyColumn("");
    setLastModifiedColumn("");
    setSoftDeleteColumn("");
  };

  const onProviderSelected = (item: SearchableDropdownListItem<string>) => {
    setProviderID(item.key);
    setSourceID("");
    resetPreviewState();
  };

  const onSourceSelected = (item: SearchableDropdownListItem<string>) => {
    setSourceID(item.key);
    resetPreviewState();
  };

  const previewSource = async (options?: { isRefresh?: boolean }) => {
    if (!formRef.current?.reportValidity() || !sourceID || !previewRequest) {
      return;
    }

    const isRefresh = options?.isRefresh ?? false;
    setPreviewError(undefined);

    try {
      const result = await previewRows.previewSourceRows(sourceID, previewRequest);
      setPreview(result);
      setPreviewRequestKey(currentPreviewRequestKey);

      const columnNames = new Set(result.schema.map((field) => field.name));
      if (!isRefresh || !columnNames.has(primaryKeyColumn)) {
        setPrimaryKeyColumn("");
      }
      if (!isRefresh || !columnNames.has(lastModifiedColumn)) {
        setLastModifiedColumn("");
      }
      if (!isRefresh || !columnNames.has(softDeleteColumn)) {
        setSoftDeleteColumn("");
      }
    } catch (error) {
      if (isRefresh) {
        addToast({ kind: "ERROR", message: `Failed to refresh sample: ${getErrorMessage(error)}` });
        return;
      }

      setPreview(undefined);
      setPreviewRequestKey("");
      setPreviewError(getErrorMessage(error));
    }
  };

  const createNewDataset = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!canCreate || !previewRequest) {
      return;
    }

    const dataset: DatasetOptions = {
      ...(name.trim() ? { name: name.trim() } : {}),
      source_id: sourceID,
      frequency_minutes: Number(frequencyMinutes),
      is_enabled: isEnabled,
      type: "dimension",
      dimension: {
        primary_key_column: primaryKeyColumn,
        ...(lastModifiedColumn ? { last_modified_column: lastModifiedColumn } : {}),
        ...(softDeleteColumn ? { is_deleted_column: softDeleteColumn } : {}),
      },
      ...previewRequest,
    };

    try {
      const createdDataset = await createDataset.createDataset(dataset);
      addToast({ kind: "SUCCESS", message: "Dataset created successfully!" });
      navigate(`/import/datasets/${createdDataset.id}`);
    } catch {
      addToast({ kind: "ERROR", message: "Dataset creation failed." });
    }
  };

  const saveDataset = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!datasetId) {
      return;
    }

    try {
      await updateDataset.updateDataset(datasetId, buildUpdateDatasetOptions({ name, frequencyMinutes, isEnabled }));
      addToast({ kind: "SUCCESS", message: "Dataset updated successfully!" });
      onSaved?.();
      navigate(cancelPath);
    } catch (error) {
      addToast({ kind: "ERROR", message: getErrorMessage(error, "Dataset update failed.") });
    }
  };

  if (isEditing && existingDataset) {
    return (
      <form className="space-y-8" onSubmit={saveDataset} ref={formRef}>
        <StepEdit
          dataset={existingDataset}
          isEnabled={isEnabled}
          name={name}
          onCancel={() => navigate(cancelPath)}
          onFrequencyChange={setFrequencyMinutes}
          onNameChange={setName}
          onSetEnabled={setIsEnabled}
          selectedFrequency={selectedFrequency}
          syncFrequencyItems={syncFrequencyItems}
          updateDatasetPending={updateDataset.isUpdating}
        />
      </form>
    );
  }

  if (providersQuery.isLoading) {
    return <Spinner />;
  }

  return (
    <form className="space-y-8" onSubmit={createNewDataset} ref={formRef}>
      {!!providersQuery.error && <Alert kind={AlertKind.ERROR}>Failed to load providers.</Alert>}
      {!!sourcesQuery.error && providerID && <Alert kind={AlertKind.ERROR}>Failed to load sources.</Alert>}
      {!!sourceTablesQuery.error && sourceID && method === "table" && (
        <Alert kind={AlertKind.ERROR}>Failed to load source tables.</Alert>
      )}
      {!!previewError && <Alert kind={AlertKind.ERROR}>{previewError}</Alert>}

      <StepHeader step={step} />

      {step === 1 ? (
        <StepOne
          canPreview={canPreview}
          hasSuccessfulPreview={hasSuccessfulCurrentPreview}
          isPreviewing={previewRows.isPreviewing}
          method={method}
          name={name}
          onNameChange={setName}
          isLoadingSources={providerID.length > 0 && sourcesQuery.isLoading}
          isLoadingTables={sourceTablesQuery.isLoading}
          onMethodChange={(nextMethod) => {
            setMethod(nextMethod);
            resetPreviewState();
          }}
          onProviderSelected={onProviderSelected}
          onRefreshTables={sourceTablesQuery.refresh}
          onSourceSelected={onSourceSelected}
          onSourceTableChange={(value) => {
            setSourceTable(value);
            resetPreviewState();
          }}
          onSqlQueryChange={(value) => {
            setSqlQuery(value);
            resetPreviewState();
          }}
          preview={preview}
          previewSource={previewSource}
          providerID={providerID}
          providerItems={providerItems}
          selectedProvider={selectedProvider}
          selectedSource={selectedSource}
          sourceID={sourceID}
          sourceItems={sourceItems}
          sourceTable={sourceTable}
          sqlQuery={sqlQuery}
          tableSuggestions={tableSuggestions}
        />
      ) : (
        preview && (
          <StepTwo
            createDatasetPending={createDataset.isCreating}
            datasetKind={datasetKind}
            isEnabled={isEnabled}
            lastModifiedColumn={lastModifiedColumn}
            onBack={() => setStep(1)}
            onCancel={() => navigate("/import/datasets")}
            onDatasetKindChange={setDatasetKind}
            onFrequencyChange={setFrequencyMinutes}
            onPrimaryKeyColumnChange={setPrimaryKeyColumn}
            onRefreshSample={() => previewSource({ isRefresh: true })}
            onSetEnabled={setIsEnabled}
            onSoftDeleteColumnChange={setSoftDeleteColumn}
            onLastModifiedColumnChange={setLastModifiedColumn}
            preview={preview}
            previewPending={previewRows.isPreviewing}
            primaryKeyColumn={primaryKeyColumn}
            selectedFrequency={selectedFrequency}
            softDeleteColumn={softDeleteColumn}
            syncFrequencyItems={syncFrequencyItems}
          />
        )
      )}

      {step === 1 && (
        <FormFooter>
          <Button
            type={ButtonStyle.TERTIARY}
            text="Cancel"
            onClick={() => navigate("/import/datasets")}
            disabled={previewRows.isPreviewing || createDataset.isCreating}
          />
          <Button
            type={ButtonStyle.PRIMARY}
            text="Continue"
            onClick={() => setStep(2)}
            disabled={!hasSuccessfulCurrentPreview || previewRows.isPreviewing || createDataset.isCreating}
          />
        </FormFooter>
      )}
    </form>
  );
};

const StepHeader = ({ step }: { step: DatasetFormStep }) => (
  <div className="flex items-center justify-between">
    <span className="text-sm font-medium text-gray-600">Step {step} of 2</span>
    <div className="flex items-center gap-2" aria-label={`Step ${step} of 2`}>
      <span className="size-2.5 rounded-full bg-emerald-500" />
      <span
        className={`
          size-2.5 rounded-full
          ${step === 2 ? "bg-emerald-500" : "bg-gray-300"}
        `}
      />
    </div>
  </div>
);

export default DatasetForm;
